// src/pages/Tenants.jsx
// Frontend in English. Full admin page:
// 1) Tenants CRUD
// 2) PhoneMap CRUD (number ↔ tenant mapping)
// 3) Twilio provisioning (create subaccount, list available numbers, purchase)
// Uses centralized API helper (src/lib/api.js) for JWT + base URL.
// Removes legacy "/api" prefix to align with backend mounts.

import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../lib/api";

// ------------------------------- UI Bits -------------------------------
function Badge({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        background: "#f3f4f6",
      }}
    >
      {children}
    </span>
  );
}

function Label({ children }) {
  return (
    <label style={{ display: "block", fontSize: 13, fontWeight: 600 }}>
      {children}
    </label>
  );
}
function Input(props) {
  return (
    <input
      {...props}
      style={{ ...inputStyle, ...(props.style || {}) }}
      autoComplete="off"
    />
  );
}
function Button({ children, primary, danger, ...rest }) {
  const base = {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
  };
  const theme = primary
    ? { background: "#111827", color: "#fff", borderColor: "#111827" }
    : danger
    ? { borderColor: "#fecaca", color: "#dc2626", background: "#fff7f7" }
    : {};
  return (
    <button {...rest} style={{ ...base, ...theme, ...(rest.style || {}) }}>
      {children}
    </button>
  );
}
function Alert({ kind = "error", children }) {
  const styles =
    kind === "error"
      ? {
          color: "#b91c1c",
          background: "#fef2f2",
          border: "1px solid #fee2e2",
        }
      : { color: "#065f46", background: "#ecfdf5", border: "1px solid #d1fae5" };
  return (
    <div
      style={{
        marginBottom: 16,
        fontSize: 14,
        padding: 12,
        borderRadius: 12,
        ...styles,
      }}
    >
      {children}
    </div>
  );
}
function Pill({ children, active }) {
  return (
    <span
      style={{
        fontSize: 12,
        padding: "2px 8px",
        borderRadius: 999,
        background: active ? "#d1fae5" : "#f3f4f6",
        color: active ? "#047857" : "#6b7280",
      }}
    >
      {children}
    </span>
  );
}
function Th({ children, className, style }) {
  return (
    <th
      style={{ textAlign: "left", fontWeight: 600, padding: "10px 14px", ...(style || {}) }}
      className={className}
    >
      {children}
    </th>
  );
}
function Td({ children, className, style }) {
  return (
    <td style={{ padding: "10px 14px", ...(style || {}) }} className={className}>
      {children}
    </td>
  );
}

// ------------------------------- Styles -------------------------------
const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  padding: 20,
};
const cardHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
};
const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
  marginBottom: 20,
};
const grid3 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 16,
  marginBottom: 20,
};
const inputStyle = {
  marginTop: 6,
  width: "100%",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: "8px 12px",
};
const tableWrapStyle = {
  overflow: "auto",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
};
const tableStyle = { minWidth: "100%", fontSize: 14, borderCollapse: "separate", borderSpacing: 0 };
const theadStyle = { background: "#f9fafb", color: "#6b7280" };
const rowStyle = { borderTop: "1px solid #e5e7eb" };
const emptyCell = { textAlign: "center", padding: 24, color: "#9ca3af" };

// ------------------------------- Page --------------------------------
export default function TenantsPage() {
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Header />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <TenantsPanel />
        <PhoneMapPanel />
      </div>
      <div className="grid grid-cols-1 mt-6">
        <TwilioProvisioningPanel />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "end",
      }}
    >
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: ".2px" }}>
          Setup · Tenants & Phone Numbers
        </h1>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
          Create tenants, link numbers and provision Twilio resources.
        </p>
      </div>
      <div style={{ display: "flex", gap: 8, fontSize: 12, color: "#6b7280" }}>
        <Badge>Version: MVP+</Badge>
        <Badge>Isolation: per tenantId</Badge>
      </div>
    </div>
  );
}

// ----------------------------- TENANTS -------------------------------
function TenantsPanel() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    _id: null,
    name: "",
    slug: "",
    defaultLocale: "en-US",
    isDefault: false,
  });
  const isEditing = Boolean(form._id);

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchTenants() {
    setLoading(true);
    setMsg("");
    try {
      const data = await apiGet("/tenants");
      setTenants(Array.isArray(data) ? data : data.items || []);
    } catch (e) {
      setMsg(e.message || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      _id: null,
      name: "",
      slug: "",
      defaultLocale: "en-US",
      isDefault: false,
    });
  }

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  }

  function normalizeSlug(v) {
    return (v || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  async function onSubmit(e) {
    e.preventDefault();
    const payload = {
      name: (form.name || "").trim(),
      slug: normalizeSlug(form.slug || form.name),
      defaultLocale: form.defaultLocale || "en-US",
      isDefault: !!form.isDefault,
    };
    if (!payload.name) return alert("Name is required.");
    if (!payload.slug) return alert("Slug is required.");

    try {
      setLoading(true);
      setMsg("");
      if (isEditing) {
        await apiPut(`/tenants/${form._id}`, payload);
      } else {
        await apiPost(`/tenants`, payload);
      }
      await fetchTenants();
      resetForm();
    } catch (e) {
      setMsg(e.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }

  function onEdit(t) {
    setForm({
      _id: t._id,
      name: t.name || "",
      slug: t.slug || "",
      defaultLocale: t.defaultLocale || "en-US",
      isDefault: !!t.isDefault,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function onDelete(t) {
    if (!window.confirm(`Delete tenant "${t.name}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      setMsg("");
      await apiDelete(`/tenants/${t._id}`);
      await fetchTenants();
      if (form._id === t._id) resetForm();
    } catch (e) {
      setMsg(e.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section style={cardStyle}>
      <div style={cardHeaderStyle}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Tenants</h2>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>
          Create, edit and manage tenants
        </span>
      </div>

      <form onSubmit={onSubmit} style={grid2}>
        <div>
          <Label>Name</Label>
          <Input
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Acme Law"
          />
        </div>

        <div>
          <Label>Slug</Label>
          <Input
            name="slug"
            value={form.slug}
            onChange={(e) => setForm((s) => ({ ...s, slug: normalizeSlug(e.target.value) }))}
            placeholder="acme-law"
          />
        </div>

        <div>
          <Label>Default locale</Label>
          <Input
            name="defaultLocale"
            value={form.defaultLocale}
            onChange={onChange}
            placeholder="en-US"
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 10 }}>
          <input
            id="isDefault"
            name="isDefault"
            type="checkbox"
            checked={form.isDefault}
            onChange={onChange}
            style={{ width: 16, height: 16 }}
          />
          <label htmlFor="isDefault" style={{ fontSize: 14 }}>
            Set as default tenant (fallback)
          </label>
        </div>

        {/* <<< AQUI ESTAVA O BUG: tinha aspas extras depois do 8 >>> */}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
          <Button type="submit" primary disabled={loading}>
            {isEditing ? "Update tenant" : "Create tenant"}
          </Button>
          {isEditing && (
            <Button type="button" onClick={resetForm} disabled={loading}>
              Cancel edit
            </Button>
          )}
        </div>
      </form>

      {msg && <Alert kind={msg.startsWith("Failed") ? "error" : "success"}>{msg}</Alert>}

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead style={theadStyle}>
            <tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Default locale</Th>
              <Th>Default?</Th>
              <Th style={{ textAlign: "right" }}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 && (
              <tr>
                <td colSpan={5} style={emptyCell}>
                  No tenants found
                </td>
              </tr>
            )}
            {tenants.map((t) => (
              <tr key={t._id} style={rowStyle}>
                <Td>{t.name}</Td>
                <Td>{t.slug}</Td>
                <Td>{t.defaultLocale || "en-US"}</Td>
                <Td>
                  <Pill active={!!t.isDefault}>{t.isDefault ? "Yes" : "No"}</Pill>
                </Td>
                <Td style={{ textAlign: "right" }}>
                  <Button onClick={() => onEdit(t)}>Edit</Button>
                  <Button danger onClick={() => onDelete(t)} style={{ marginLeft: 8 }}>
                    Delete
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// --------------------------- PHONE MAPS ------------------------------
function PhoneMapPanel() {
  const [tenants, setTenants] = useState([]);
  const [phoneMaps, setPhoneMaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    _id: null,
    channel: "whatsapp", // or "sms"
    toNumberCanonical: "",
    tenantId: "",
  });
  const isEditing = Boolean(form._id);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setMsg("");
      try {
        const [tData, pData] = await Promise.all([apiGet("/tenants"), apiGet("/phonemap")]);
        setTenants(Array.isArray(tData) ? tData : tData.items || []);
        setPhoneMaps(Array.isArray(pData) ? pData : pData.items || []);
      } catch (e) {
        setMsg(e.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function resetForm() {
    setForm({ _id: null, channel: "whatsapp", toNumberCanonical: "", tenantId: "" });
  }

  function onChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  function isE164(num) {
    return /^\+[1-9]\d{6,14}$/.test(num || "");
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.tenantId) return alert("Tenant is required.");
    if (!form.toNumberCanonical) return alert("Number is required.");
    if (!isE164(form.toNumberCanonical))
      return alert("Number must be in E.164 format, e.g., +15551234567");

    const payload = {
      channel: form.channel,
      toNumberCanonical: form.toNumberCanonical,
      tenantId: form.tenantId,
    };

    try {
      setLoading(true);
      setMsg("");
      if (isEditing) {
        await apiPut(`/phonemap/${form._id}`, payload);
      } else {
        await apiPost(`/phonemap`, payload);
      }
      await fetchPhoneMaps();
      resetForm();
    } catch (e) {
      setMsg(e.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }

  async function fetchPhoneMaps() {
    setLoading(true);
    setMsg("");
    try {
      const data = await apiGet("/phonemap");
      setPhoneMaps(Array.isArray(data) ? data : data.items || []);
    } catch (e) {
      setMsg(e.message || "Failed to load phone maps");
    } finally {
      setLoading(false);
    }
  }

  function onEdit(pm) {
    setForm({
      _id: pm._id,
      channel: pm.channel || "whatsapp",
      toNumberCanonical: pm.toNumberCanonical || "",
      tenantId: pm.tenantId || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function onDelete(pm) {
    if (!window.confirm(`Delete mapping for ${pm.toNumberCanonical}?`)) return;
    try {
      setLoading(true);
      setMsg("");
      await apiDelete(`/phonemap/${pm._id}`);
      await fetchPhoneMaps();
      if (form._id === pm._id) resetForm();
    } catch (e) {
      setMsg(e.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  }

  function renderTenantName(list, id) {
    const t = list.find((x) => x._id === id);
    if (!t) return "—";
    return `${t.name} (${t.slug})`;
  }

  return (
    <section style={cardStyle}>
      <div style={cardHeaderStyle}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Phone Numbers · Tenant Mapping</h2>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>
          Link Twilio numbers to tenants
        </span>
      </div>

      <form onSubmit={onSubmit} style={grid3}>
        <div>
          <Label>Channel</Label>
          <select name="channel" value={form.channel} onChange={onChange} style={inputStyle}>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        <div>
          <Label>Number (E.164)</Label>
          <Input
            name="toNumberCanonical"
            value={form.toNumberCanonical}
            onChange={onChange}
            placeholder="+15551234567"
          />
        </div>

        <div>
          <Label>Tenant</Label>
          <select name="tenantId" value={form.tenantId} onChange={onChange} style={inputStyle}>
            <option value="">Select a tenant…</option>
            {tenants.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
          <Button type="submit" primary disabled={loading}>
            {isEditing ? "Update mapping" : "Create mapping"}
          </Button>
          {isEditing && (
            <Button type="button" onClick={resetForm} disabled={loading}>
              Cancel edit
            </Button>
          )}
        </div>
      </form>

      {msg && <Alert kind={msg.startsWith("Failed") ? "error" : "success"}>{msg}</Alert>}

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead style={theadStyle}>
            <tr>
              <Th>Channel</Th>
              <Th>Number</Th>
              <Th>Tenant</Th>
              <Th style={{ textAlign: "right" }}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {phoneMaps.length === 0 && (
              <tr>
                <td colSpan={4} style={emptyCell}>
                  No mappings found
                </td>
              </tr>
            )}
            {phoneMaps.map((pm) => (
              <tr key={pm._id} style={rowStyle}>
                <Td style={{ textTransform: "capitalize" }}>{pm.channel}</Td>
                <Td
                  style={{
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                  }}
                >
                  {pm.toNumberCanonical}
                </Td>
                <Td>{renderTenantName(tenants, pm.tenantId)}</Td>
                <Td style={{ textAlign: "right" }}>
                  <Button onClick={() => onEdit(pm)}>Edit</Button>
                  <Button danger onClick={() => onDelete(pm)} style={{ marginLeft: 8 }}>
                    Delete
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ------------------------ TWILIO PROVISIONING ------------------------
function TwilioProvisioningPanel() {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // Tenant selection for provisioning
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");

  // Subaccount SID (optional visual)
  const [subaccountSid, setSubaccountSid] = useState("");

  // Number search / purchase
  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState("");

  const canList = useMemo(() => !!tenantId && !!country, [tenantId, country]);
  const canPurchase = useMemo(() => !!tenantId && !!selected, [tenantId, selected]);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet("/tenants");
        setTenants(Array.isArray(data) ? data : data.items || []);
      } catch (e) {
        setMsg(e.message || "Failed to load tenants");
      }
    })();
  }, []);

  async function createSubaccount() {
    if (!tenantId) {
      setMsg("Select a tenant first.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const data = await apiPost(`/tenants/${tenantId}/twilio/subaccount`, {});
      if (data?.subaccountSid) setSubaccountSid(data.subaccountSid);
      setMsg(
        data?.message ||
          `Subaccount created${data?.subaccountSid ? `: ${data.subaccountSid}` : ""}.`
      );
    } catch (e) {
      setMsg(e.message || "Failed to create subaccount");
    } finally {
      setBusy(false);
    }
  }

  async function listAvailable() {
    if (!canList) {
      setMsg("Select tenant and country first.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const query = new URLSearchParams({
        country,
        ...(areaCode ? { areaCode } : {}),
      }).toString();
      const data = await apiGet(
        `/tenants/${tenantId}/twilio/available-numbers?${query}`
      );
      const nums = Array.isArray(data) ? data : data?.numbers || [];
      setAvailable(nums);
      setMsg(
        `Found ${nums.length} numbers${areaCode ? ` in area ${areaCode}` : ""}.`
      );
    } catch (e) {
      setMsg(e.message || "Failed to list numbers");
    } finally {
      setBusy(false);
    }
  }

  async function purchaseNumber() {
    if (!canPurchase) {
      setMsg("Select a number first.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const data = await apiPost(
        `/tenants/${tenantId}/twilio/purchase-number`,
        { phoneNumber: selected }
      );
      setMsg(
        data?.message ||
          `Number ${selected} purchased. You're ready to send/receive.`
      );
    } catch (e) {
      setMsg(e.message || "Failed to purchase number");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={cardStyle}>
      <div style={cardHeaderStyle}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Twilio Provisioning</h2>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>
          Create a subaccount, list available numbers and purchase
        </span>
      </div>

      {msg && <Alert kind={msg.startsWith("Failed") ? "error" : "success"}>{msg}</Alert>}

      <div style={grid3}>
        <div>
          <Label>Tenant</Label>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            style={inputStyle}
          >
            <option value="">Select a tenant…</option>
            {tenants.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Country</Label>
          <Input
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="US"
          />
        </div>
        <div>
          <Label>Area code (optional)</Label>
          <Input
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value)}
            placeholder="310"
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Button onClick={createSubaccount} primary disabled={busy || !tenantId}>
          {busy ? "Working..." : "Create subaccount"}
        </Button>
        <Button onClick={listAvailable} disabled={busy || !canList}>
          {busy ? "Working..." : "List available"}
        </Button>
      </div>

      {subaccountSid ? (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          Subaccount SID: {subaccountSid}
        </div>
      ) : null}

      {available?.length ? (
        <div>
          <Label>Select a number</Label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={inputStyle}
          >
            <option value="">-- choose a number --</option>
            {available.map((n) => {
              const val = typeof n === "string" ? n : n.phoneNumber || n.friendlyName;
              return (
                <option key={val} value={val}>
                  {val}
                </option>
              );
            })}
          </select>
          <div style={{ marginTop: 10 }}>
            <Button onClick={purchaseNumber} primary disabled={busy || !canPurchase}>
              {busy ? "Working..." : "Purchase number"}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
